import { Page } from '../../..';
import { VehicleType } from '../../../resources/base/models/vehicleTypes';
import { GetDealerDocumentsList } from '../../../types/dealerDocuments';
import { MilesModelsResponse } from '../../../types/vehicleModels';
import { getTransactionId } from '../../common/data';
import { MilesDocumentTemplatesResponse } from '../../common/miles-pos-quote-integration/models';
import { GetRunningContractsResponse } from '../../common/miles-pos-running-contract-integration-service/models';
import { waitForMiles } from '../../common/miscUtils';
import { store } from '../../redux';
import { setQuoteData, updateQuoteIdAndStatus } from '../../redux/features/quote.slice';
import { callFes, callFesWithLoading } from '../index';
import {
  CreateQuoteRequest,
  CustomerSearchRequest,
  DownloadStipulationDocumentResponse,
  MilesCancelQuoteResponse,
  MilesCustomerSearchResponse,
  MilesGetVehicleRegistrationDetailsResponse,
  MilesPatchVehicleRegistrationDetailsBody,
  MilesPatchVehicleRegistrationDetailsResponse,
  MilesPendingContractsListResponse,
  MilesPendingContractsSingleResponse,
  MilesSetVehicleLicensePlateData,
  PendingContractStipulationDocumentsResponse,
  PrintDocumentsResponse,
  SalesforceMailResponse,
  SearchQuotesResponse,
  UpdateExistingBusinessRequest,
  UpdateExistingCustomerRequest,
  UploadStipulationDocumentRequestBody,
  UploadStipulationDocumentResponse,
} from './types';

export const getVehicleManufacturers = async (vehicleType?: VehicleType) => {
  const includeVehicleType = vehicleType ? `?include${vehicleType === VehicleType.STOCK ? 'Stock' : 'Used'}=true` : '';
  const response = await callFes.getMiles<VehicleIntegration.Components.Schemas.GetManufacturersResponse>(
    `manufacturers${includeVehicleType}`
  );
  return (response?.vehicleData ?? [])
    .map((item) => item?.model?.manufacturer)
    .filter(Boolean) as VehicleIntegration.Components.Schemas.ManufacturerType[];
};

export const getVehicleGroupModels = async (
  manufacturer: string,
  vehicleType: string,
  modelYearLowerLimit: string,
  modelYearUpperLimit: string
): Promise<MilesModelsResponse> => {
  const modelYearLimitQuery = `modelYearLowerLimit=${modelYearLowerLimit}&modelYearUpperLimit=${modelYearUpperLimit}`;
  const baseUrl = `?manufacturer=${manufacturer}&type=${vehicleType}&${modelYearLimitQuery}&includeExpired=true`;
  const url = vehicleType === VehicleType.USED ? `${baseUrl}&includeUsed=true` : baseUrl;
  return callFes.getMiles(`models${url}`);
};

export const getUsedStockVehiclesData = async (
  milesModelId: string,
  vehicleType: string,
  modelYearLowerLimit: string,
  modelYearUpperLimit: string
): Promise<Required<VehicleIntegration.Components.Schemas.GetCatalogVehicleResponse>> => {
  const modelYearLimitQuery = `modelYearLowerLimit=${modelYearLowerLimit}&modelYearUpperLimit=${modelYearUpperLimit}`;
  const baseUrl = `?milesModelId=${milesModelId}&type=${vehicleType}&${modelYearLimitQuery}&includeExpired=true`;
  const url = vehicleType === VehicleType.USED ? `${baseUrl}&includeUsed=true` : baseUrl;
  return callFes.getMiles(`catalog-vehicles${url}`);
};

export const getBTOVehiclesData = async (
  jatoId: string,
  vehicleType: string
): Promise<VehicleIntegration.Components.Schemas.GetCatalogVehicleResponse> => {
  return callFes.getMiles(`catalog-vehicles?jatoId=${jatoId}&type=${vehicleType}&includeExpired=true`);
};

export const getCatalogVehiclesByModelId = async (
  milesModelId: string
): Promise<VehicleIntegration.Components.Schemas.GetCatalogVehicleResponse> => {
  return callFes.getMiles(`catalog-vehicles?milesModelId=${milesModelId}`);
};

export const searchCustomerData = async (
  prospectData: CustomerSearchRequest,
  currentPage: number
): Promise<MilesCustomerSearchResponse> => {
  return callFes.postMiles(`customers?page=${currentPage}`, prospectData);
};

export const createOrUpdatePrivateCustomer = async (
  requestBody: UpdateExistingCustomerRequest,
  quoteId: string
): Promise<{ milesCustomerId: string }> => {
  return callFes.postMiles(`quote/${quoteId}/customer`, requestBody);
};

export const createCoCustomerAndLinkToTheQuote = async (
  requestBody: UpdateExistingCustomerRequest,
  quoteId: string
): Promise<{ milesCustomerId: string }> => {
  return callFes.postMiles(`quote/${quoteId}/co-customer-data`, requestBody);
};

export const unlinkCoCustomerFromTheQuote = async (
  quoteId: string,
  milesCustomerId: string
): Promise<{ milesCustomerId: string }> => {
  return callFes.deleteMiles(`quote/${quoteId}/co-customer-unlink/${milesCustomerId}`);
};

export const createOrUpdateBusinessCustomer = async (
  requestBody: UpdateExistingBusinessRequest,
  quoteId: string
): Promise<{ milesCustomerId: string }> => {
  return callFes.postMiles(`quote/${quoteId}/business-data`, requestBody);
};

export const createQuote = async (data: CreateQuoteRequest): Promise<QuoteSchema.Schemas.CreateQuoteResponse> => {
  return callFes.postMiles('quote', data);
};

export const getQuoteQuotationTemplates = async (
  quoteId: string
): Promise<Required<QuoteSchema.Schemas.GetQuotationTemplatesResponse>> => {
  return callFes.getMiles(`quotes/${quoteId}/quotation-templates`);
};

export const getQuotationTemplates = async (): Promise<Required<QuoteSchema.Schemas.GetQuotationTemplatesResponse>> => {
  return callFesWithLoading.getMiles(`quotation-templates`);
};

export const getQuote = async (quoteId: string): Promise<Required<QuoteSchema.Schemas.QuoteResponse>> => {
  return callFes.getMiles(`quotes/${quoteId}`);
};

export const getQuotes = async (data: Partial<Page>): Promise<SearchQuotesResponse> => {
  return callFesWithLoading.postMiles(`quotes/search`, data);
};

export const getContracts = async (data: Partial<Page>): Promise<GetRunningContractsResponse> => {
  return callFes.postMiles(`quotes/contracts`, data);
};

export const linkStakeholderContact = async (
  quoteId: string,
  data: QuoteSchema.Schemas.LinkStakeholderContactToSalesQuoteRequest
) => {
  return callFes.postMiles(`quotes/${quoteId}/stakeholder-contacts`, data);
};

export const unlinkStakeholderContact = async (quoteId: string, contactId: string) => {
  return callFes.deleteMiles(`quotes/${quoteId}/stakeholder-contacts/${contactId}`);
};

export const getQuoteStakeholderContacts = async (
  quoteId: string
): Promise<QuoteSchema.Schemas.GetStakeholderContactToSalesQuoteResponse> => {
  return callFes.getMiles(`quotes/${quoteId}/stakeholder-contacts`);
};

export const getDealerContacts = async (
  milesCompanyId: string
): Promise<DealerIntegration.Components.Schemas.GetDealerContactsResponse> => {
  return callFesWithLoading.getMiles(`dealer-contacts/${milesCompanyId}`);
};

export const getCurrentDealerContacts =
  async (): Promise<DealerIntegration.Components.Schemas.GetDealerContactsResponse> => {
    return callFesWithLoading.getMiles(`dealer-contacts/current`);
  };

export const getAllowedBrokerCompanies =
  async (): Promise<DealerIntegration.Components.Schemas.GetDealerContactsResponse> => {
    return callFes.getMiles(`broker-contact/current/allowed-broker-companies`);
  };

export const getBrokerLoginLink = async (brokerCompanyId: string): Promise<{ url: string }> => {
  return callFes.getMiles(`broker-contact/current/allowed-broker-companies/${brokerCompanyId}/redirect-link`);
};

export const updateQuoteSalesPerson = async (salesPersonId: string, quoteId: string): Promise<void> => {
  return callFes.postMiles(`dealer-contacts`, { salesPersonId, quoteId });
};

/**
 * - Edit request creates copy of the quote assigning it new status.
 * - Response has only certain metadata including new quoteId.
 * - Rest of the quote data stays the same and is not returned.
 */
export const editQuote = async (id: string): Promise<QuoteSchema.Schemas.EditSalesQuoteResponse> => {
  const quoteFragment = await callFesWithLoading.postMiles(`quotes/${id}/edit`);
  await waitForMiles();
  store.dispatch(updateQuoteIdAndStatus(quoteFragment));
  return quoteFragment;
};

export const copyQuote = async (id: string): Promise<QuoteSchema.Schemas.CreateQuoteResponse> => {
  return callFes.postMiles(`quotes/${id}/copy`);
};

export const confirmationQuote = async (id: string): Promise<QuoteSchema.Schemas.EditSalesQuoteResponse> => {
  return callFes.postMiles(`quotes/${id}/confirmation`);
};

export const calculateQuote = async (
  quoteId: string,
  request: QuoteSchema.Schemas.CalculateSalesQuoteRequest
): Promise<Required<QuoteSchema.Schemas.QuoteResponse>> => {
  const quote = await callFes.postMiles(`quote/${quoteId}/calculate`, request);
  store.dispatch(setQuoteData(quote));
  return quote;
};

export const cancelQuote = async (id: string): Promise<MilesCancelQuoteResponse> => {
  return callFes.postMiles(`quotes/${id}/cancel`);
};

export const validateQuote = async (quoteId: string): Promise<QuoteSchema.Schemas.EditSalesQuoteResponse> => {
  const quoteFragmnet = await callFesWithLoading.postMiles(`quotes/${quoteId}/validate`);
  store.dispatch(updateQuoteIdAndStatus(quoteFragmnet));
  return quoteFragmnet;
};

// Integration added in PC-2761 - intended for future use.
// export const getDocumentTemplatesPendingContracts = async (
//   pendingContractId: string
// ): Promise<MilesDocumentTemplatesResponse> => {
//   const url = `${REACT_APP_API_GATEWAY}/miles/${REACT_APP_BACKEND_BRAND}/pending-contracts/${pendingContractId}/document-templates`;
//   return axiosWithToken
//     .get(url)
//     .then((response) => {
//       return response.data;
//     })
//     .catch((error) => {
//       return Promise.reject(error);
//     });
// };

export const getDocumentTemplates = async (quoteId: string): Promise<MilesDocumentTemplatesResponse> => {
  return callFes.getMiles(`quotes/${quoteId}/document-templates`);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const printQuoteDocuments = async (quoteId: string, documentIds: string[]): Promise<PrintDocumentsResponse> => {
  const dataStorageId = getTransactionId();
  return callFes.postMiles(`quotes/${quoteId}/documents/print`, { transactionId: dataStorageId, documentIds });
};

export const sendQuoteDocuments = async (quoteId: string, documentIds: string[]): Promise<SalesforceMailResponse> => {
  const dataStorageId = getTransactionId();
  return callFes.postMiles(`quotes/${quoteId}/documents/email`, { transactionId: dataStorageId, documentIds });
};

export const getQuoteVehicleOptions = async (
  quoteId: string
): Promise<VehicleIntegration.Components.Schemas.GetVehicleOptionsResponse> => {
  return callFes.getMiles(`quotes/${quoteId}/vehicle-options`);
};

export const getPendingContracts = async (body): Promise<MilesPendingContractsListResponse> => {
  return callFes.postMiles('pending-contracts', body);
};

export const getPendingContractById = async (contractId: string): Promise<MilesPendingContractsSingleResponse> => {
  return callFes.getMiles(`pending-contracts/${contractId}`);
};

export const updateSalesPersonPendingContract = async (
  salesPersonId: string,
  pendingContractId: string
): Promise<void> => {
  return callFes.postMiles(`update-dealer-contacts`, { salesPersonId, pendingContractId });
};

export const getPendingContractRegistrationDetails = async (
  contractId: string
): Promise<MilesGetVehicleRegistrationDetailsResponse> => {
  return callFes.getMiles(`pending-contracts/${contractId}/registration-details`);
};

export const updatePendingContractRegistrationDetails = async (
  contractId: string,
  details: MilesPatchVehicleRegistrationDetailsBody
): Promise<MilesPatchVehicleRegistrationDetailsResponse> => {
  return callFes.patchMiles(`pending-contracts/${contractId}/registration-details`, details);
};

export const setPendingContractLicensePlates = async (
  contractId: string,
  details: MilesSetVehicleLicensePlateData
): Promise<MilesSetVehicleLicensePlateData> => {
  return callFes.postMiles(`pending-contracts/${contractId}/registration-details/license-plate`, details);
};

export const getPendingContractStipulations = async (
  contractId: string
): Promise<MilesPosContractIntegrationService.Components.Schemas.GetContractsStipulationsResponse> => {
  return callFes.getMiles(`pending-contracts/${contractId}/stipulations`);
};

export const getPendingContractStipulationDocuments = async (
  contractId: string
): Promise<PendingContractStipulationDocumentsResponse> => {
  return callFes.getMiles(`pending-contracts/${contractId}/stipulations/documents`);
};

export const uploadPendingContractStipulationDocuments = async (
  contractId: string,
  stipulationId: string,
  data: UploadStipulationDocumentRequestBody,
  onUploadProgress
): Promise<UploadStipulationDocumentResponse> => {
  const config = {
    onUploadProgress(progressEvent) {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onUploadProgress(percentCompleted, progressEvent.loaded, progressEvent.total);
    },
  };
  return callFes.postMiles(
    `pending-contracts/${contractId}/stipulations/${stipulationId}/documents`,
    data,
    false,
    config
  );
};

export const getPendingContractStipulationSingleDocument = async (
  contractId: string,
  stipulationId: string,
  documentId: string
): Promise<DownloadStipulationDocumentResponse> => {
  return callFes.getMiles(`pending-contracts/${contractId}/stipulations/${stipulationId}/documents/${documentId}`);
};

export const deletePendingContractStipulationSingleDocument = async (
  contractId: string,
  stipulationId: string,
  documentId: string
): Promise<void> => {
  return callFes.deleteMiles(`pending-contracts/${contractId}/stipulations/${stipulationId}/documents/${documentId}`);
};

export const setPendingContractPreparation = async (contractId: string): Promise<void> => {
  return callFes.postMiles(`pending-contracts/${contractId}/vehicle-deliveries/preparations`);
};

export const cancelPendingContract = async (contractId: string): Promise<void> => {
  return callFes.postMiles(`pending-contracts/${contractId}/cancel`);
};

export const getDealerDocumentList = async (
  selectedCompany: string,
  dateFrom: string,
  dateTo: string,
  page: number,
  pageSize: number = 20
): Promise<GetDealerDocumentsList> => {
  return callFes.postMiles(`dealer-documents/${selectedCompany}`, { dateFrom, dateTo, page, pageSize });
};

export const getDealerDocumentFile = async (
  selectedCompany: string = '',
  documentId: string
): Promise<DealerIntegration.Components.Schemas.GetDocumentResponse> => {
  return callFes.getMiles(`dealer-documents/${selectedCompany}/${documentId}`);
};

export const getQuoteCoCustomers = async (quoteId: string): Promise<QuoteSchema.Schemas.GetCoCustomersResponse> => {
  return callFesWithLoading.getMiles(`quotes/${quoteId}/co-customers`);
};

export const setQuoteBankAccount = async (
  quoteId: string,
  data: CustomerIntegration.Schemas.PostAddBankAccountRequest
): Promise<CustomerIntegration.Schemas.PostAddBankAccountRequest> => {
  return callFesWithLoading.putMiles(`quotes/${quoteId}/bank-accounts`, data);
};
